// frontend/src/components/shared/EliteError.tsx
/**
 * Elite Error State Component
 * Professional error handling with recovery actions
 */

import React from 'react';
import { AlertCircle, Clock3, RefreshCw, LifeBuoy, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface EliteErrorProps {
  error?: string;
  title?: string;
  onRetry?: () => void;
  onHelp?: () => void;
  onHome?: () => void;
  showTroubleshooting?: boolean;
  retryDisabled?: boolean;
  retryLabel?: string;
  variant?: 'connection' | 'data' | 'auth' | 'rateLimit' | 'generic';
}

export const EliteError: React.FC<EliteErrorProps> = ({
  error,
  title,
  onRetry,
  onHelp,
  onHome,
  showTroubleshooting = true,
  retryDisabled = false,
  retryLabel,
  variant = 'generic'
}) => {
  const { t } = useTranslation();
  const getVariantConfig = () => {
    switch (variant) {
      case 'connection':
        return {
          title: t('errorConnectionInterrupted'),
          message: t('errorConnectionMessage'),
          color: 'var(--error-500)',
          actionColor: 'var(--error-600)',
          icon: AlertCircle
        };
      case 'data':
        return {
          title: t('errorDataLoadingTitle'),
          message: t('errorDataLoadingMessage'),
          color: 'var(--warning-500)',
          actionColor: 'var(--warning-700)',
          icon: AlertCircle
        };
      case 'auth':
        return {
          title: t('errorAuthenticationTitle'),
          message: t('errorAuthenticationMessage'),
          color: 'var(--primary-400)',
          actionColor: 'var(--primary-600)',
          icon: AlertCircle
        };
      case 'rateLimit':
        return {
          title: t('reportsRateLimitedTitle'),
          message: t('reportsRateLimitedReady'),
          color: 'var(--warning-500)',
          actionColor: 'var(--primary-600)',
          icon: Clock3
        };
      default:
        return {
          title: t('errorGenericTitle'),
          message: t('errorGenericMessage'),
          color: 'var(--text-tertiary)',
          actionColor: 'var(--primary-600)',
          icon: AlertCircle
        };
    }
  };

  const config = getVariantConfig();
  const Icon = config.icon;

  return (
    <div
      className="elite-error"
      style={{
        background: 'var(--bg-elevated)',
        borderRadius: '8px',
        padding: 'clamp(24px, 5vw, 40px) clamp(18px, 4vw, 32px)',
        border: '1px solid var(--border-primary)',
        boxShadow: 'var(--shadow-lg)',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}
    >
      {/* Animated Icon */}
      <div
        style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 24px',
          borderRadius: '50%',
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--border-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'errorPulse 2s infinite ease-in-out',
          position: 'relative'
        }}
      >
        <Icon size={40} color={config.color} />
        <div
          style={{
            position: 'absolute',
            inset: -8,
            border: `1px solid ${config.color}`,
            borderRadius: '50%',
            opacity: 0.2,
            animation: 'errorGlow 2s infinite ease-in-out'
          }}
        />
      </div>

      <h3
        style={{
          fontSize: '24px',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: '12px',
          letterSpacing: '-0.01em'
        }}
      >
        {title || config.title}
      </h3>

      <p
        style={{
          fontSize: '15px',
          color: 'var(--text-secondary)',
          marginBottom: '24px',
          lineHeight: 1.6,
          maxWidth: '400px',
          margin: '0 auto 24px'
        }}
      >
        {error || config.message}
      </p>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {onRetry && (
          <button
            type="button"
            className="elite-error__retry"
            onClick={onRetry}
            disabled={retryDisabled}
            aria-disabled={retryDisabled}
            style={{
              padding: '14px 32px',
              minHeight: '44px',
              background: config.actionColor,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '15px',
              cursor: retryDisabled ? 'not-allowed' : 'pointer',
              opacity: retryDisabled ? 0.65 : 1,
              boxShadow: 'var(--shadow-sm)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (retryDisabled) return;
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}
            onMouseLeave={(e) => {
              if (retryDisabled) return;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
            }}
          >
            <RefreshCw size={18} />
            {retryLabel || t('autogen.90d93d7e')}
          </button>
        )}

        {onHelp && (
          <button
            type="button"
            className="elite-error__help"
            onClick={onHelp}
            style={{
              padding: '14px 32px',
              minHeight: '44px',
              background: 'var(--status-panel-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-secondary)',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = config.color;
              e.currentTarget.style.background = 'var(--status-panel-hover-bg)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-secondary)';
              e.currentTarget.style.background = 'var(--status-panel-bg)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <LifeBuoy size={18} color={config.color} />
            {t('autogen.e570be77')}
          </button>
        )}

        {onHome && (
          <button
            type="button"
            onClick={onHome}
            style={{
              padding: '14px 32px',
              minHeight: '44px',
              background: 'var(--status-panel-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--status-panel-hover-bg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--status-panel-bg)';
            }}
          >
            <Home size={18} />
            {t('autogen.19445d72')}
          </button>
        )}
      </div>

      {/* Troubleshooting Tips */}
      {showTroubleshooting && variant === 'connection' && (
        <details
          className="elite-error__troubleshooting"
          style={{
            marginTop: '32px',
            padding: '16px 18px',
            background: 'var(--status-panel-bg)',
            borderRadius: '8px',
            border: '1px solid var(--border-primary)',
            textAlign: 'left'
          }}
        >
          <summary
            style={{
              cursor: 'pointer',
              listStylePosition: 'inside',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--text-primary)'
            }}
          >
            {t('autogen.759152a4')}
          </summary>
          <ul
            style={{
              margin: '12px 0 0',
              paddingLeft: 20,
              fontSize: '13px',
              color: 'var(--text-secondary)',
              lineHeight: 1.8
            }}
          >
            <li>{t('autogen.946fdb3a')}</li>
            <li>{t('autogen.bc618612')}</li>
            <li>{t('autogen.a478430a')}</li>
            <li>{t('autogen.7cfce5f5')}</li>
          </ul>
        </details>
      )}

      <style>{`
        @keyframes errorPulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes errorGlow {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

export default EliteError;
